import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR, RAG_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { type ReportRange, reportApi, reportKeys } from '../api';

/**
 * Cycle time scatter + control chart — spec 16.2.
 *
 * Hai biểu đồ trong một khung vì chúng đọc **cùng một** dữ liệu và trả lời hai
 * câu hỏi kề nhau: "task mất bao lâu" (scatter + percentile) và "quy trình có
 * ổn định không" (trung bình động ± 2σ).
 *
 * Đường **p85** là đường đáng nhìn nhất: p50 để hiểu, p85 để hứa. "85% task xong
 * trong 9 ngày" là câu nói được với khách; "trung bình 4 ngày" thì không.
 */
export function CycleTimeChart({
  projectId,
  range,
}: {
  projectId: string;
  range: ReportRange;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.cycleTime(projectId, range),
    queryFn: () => reportApi.cycleTime(projectId, range),
  });

  const items = report.data?.items ?? [];
  const cycle = report.data?.cycle ?? null;

  return (
    <ChartFrame
      title={t('reports.cycleTitle')}
      help="cycle"
      subtitle={
        cycle
          ? t('reports.cycleSubtitle', { p50: cycle.p50, p85: cycle.p85 })
          : t('reports.cycleNoData')
      }
      asOf={range.asOf ? new Date(range.asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        report.data && items.length === 0
          ? { title: t('reports.noCompleted'), description: t('reports.noCompletedHelp') }
          : null
      }
      legend={[
        { label: t('reports.cycleLegend'), color: FLOW_VAR.inProgress },
        { label: t('reports.leadLegend'), color: FLOW_VAR.todo },
        { label: t('reports.p85Legend'), color: RAG_VAR.red },
      ]}
    >
      <div className="flex flex-col gap-3">
        <div className="h-44 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="completedOn" tick={{ fontSize: 11 }} stroke="var(--color-border)" />
              <YAxis
                dataKey="cycleTimeDays"
                name={t('reports.cycleLegend')}
                unit="d"
                tick={{ fontSize: 11 }}
                stroke="var(--color-border)"
              />
              <ZAxis range={[50, 50]} />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  fontSize: 12,
                }}
                formatter={(value: unknown, name: unknown) => [String(value), String(name)]}
              />
              {/* Percentile vẽ thành đường ngang: một con số trong tiêu đề dễ bị
                  bỏ qua, một đường cắt ngang đám điểm thì không. */}
              {cycle && (
                <ReferenceLine
                  y={cycle.p85}
                  stroke={RAG_VAR.red}
                  strokeDasharray="4 4"
                  label={{ value: `p85 ${cycle.p85}d`, fontSize: 11, fill: RAG_VAR.red }}
                />
              )}
              {cycle && (
                <ReferenceLine y={cycle.p50} stroke={FLOW_VAR.inProgress} strokeDasharray="2 4" />
              )}
              <Scatter data={items} name={t('reports.cycleLegend')} fill={FLOW_VAR.inProgress} />
              <Scatter
                data={items.map((item) => ({ ...item, cycleTimeDays: item.leadTimeDays }))}
                name={t('reports.leadLegend')}
                fill={FLOW_VAR.todo}
                fillOpacity={0.5}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        <div className="h-36 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={report.data?.controlChart ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-border)" />
              <YAxis tick={{ fontSize: 11 }} stroke="var(--color-border)" />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  fontSize: 12,
                }}
              />
              <Line
                type="monotone"
                dataKey="average"
                name={t('reports.controlAverage')}
                stroke={FLOW_VAR.inProgress}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="upper"
                name={t('reports.controlUpper')}
                stroke={RAG_VAR.unknown}
                strokeDasharray="4 4"
                dot={false}
              />
              {/* Điểm ra ngoài dải tô đỏ: đó là tín hiệu quy trình có gì đổi,
                  không phải một ngày xui. */}
              <Line
                type="monotone"
                dataKey="value"
                name={t('reports.controlValue')}
                stroke={RAG_VAR.red}
                dot={{ r: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </ChartFrame>
  );
}
